import { forwardRef, HtmlHTMLAttributes, memo, ReactNode } from "react";
import classNames from "classnames";
import { Avatar, AvatarPropsType } from "../../Avatar";
import { RelativeTimeRenderer, RelativeTimeRendererProps } from "../../dateTimeRenderers";
import { mapFeedItemSizeToAvatarSize } from "./utils";

import styles from "./style.module.scss";

export type FeedItemBaseSize = "sm" | "md";

export type FeedItemBaseProps = HtmlHTMLAttributes<HTMLDivElement> & {
  size?: FeedItemBaseSize
  text?: ReactNode
  supportingText?: ReactNode
  additionalContent?: ReactNode
  newDot?: boolean
  connector?: boolean
  avatarProps?: AvatarPropsType
  timestamp?: RelativeTimeRendererProps["value"]
  textClassName?: string,
  timeClassName?: string,
  supportingTextClassName?: string,
};

export const FeedItemBase = memo(forwardRef<HTMLDivElement, FeedItemBaseProps>((
  {
    size = "sm",
    text,
    supportingText,
    additionalContent,
    newDot,
    connector,
    avatarProps,
    timestamp,
    textClassName,
    timeClassName,
    supportingTextClassName,
    ...props
  },
  ref,
) => {
  const header = text ?? avatarProps?.userName;
  return (
    <div
      {...props}
      style={{
        gridTemplateRows: `auto ${[ additionalContent, connector ].filter(Boolean)
          .map(_ => "auto")
          .join(" ")}`,
        ...props.style
      }}
      className={classNames(
        styles.root,
        props.className,
        {
          [styles.newDot]: newDot,
          [styles.connector]: connector,
        },
      )}
      ref={ref}
    >
      <Avatar
        key="avatar"
        avatarSize={mapFeedItemSizeToAvatarSize(size)}
        {...avatarProps}
        className={classNames(styles.avatar, avatarProps?.className)}
      />
      <div key="headerBlock">
        <div key="text" className={classNames(styles.text, textClassName)}>
          {typeof header === "string"
            ? <span key="header">{header}</span>
            : header
          }
          <RelativeTimeRenderer
            key="time"
            className={classNames(styles.time, timeClassName)}
            value={timestamp ?? null}
          />
        </div>
        {supportingText &&
          <div
            key="supportingText"
            className={classNames(styles.supportingText, supportingTextClassName)}
          >
            {supportingText}
          </div>
        }
      </div>
      {additionalContent}
    </div>
  );
}));
